/**
 * Melbourne verdict seed data.
 *
 * DUPLICATION, not abstraction. This file is a parallel sibling of
 * `verdicts.ts` (Hyderabad) and uses the exact same schema. The decision
 * to keep two files instead of one generic dataset is deliberate — it
 * insulates Hyderabad from any Melbourne data change and vice versa.
 *
 * Schema: v2 (confidenceScore + tags + sources) — mirrors HYD.
 * Data is fake-but-plausible. Prices are AUD per m² unless noted.
 */

import type { Verdict, VerdictLevel, VerdictSource } from "./verdicts";
import { MEL_SUBURB_PRICING, getPricingFor } from "./suburb-pricing";

export type { Verdict, VerdictLevel, VerdictSource };

/** City default center for Melbourne (CBD, lng/lat). */
export const MEL_CENTER: [number, number] = [144.9631, -37.8136];

// See `verdicts.ts` for the same back-fill pattern — inline fields win,
// bank back-fills the rest so every MEL suburb resolves to a researched
// median rather than a generic band fallback.
const _MEL_RAW: Verdict[] = [
  // --- CBD / inner core ---
  {
    id: "mel-cbd",
    name: "Melbourne CBD",
    coord: [144.9631, -37.8136],
    verdict: "amber",
    reasons: [
      "Free tram zone + 6 train stations; nothing beats this for car-free living",
      "Apartment resale flat-to-down 3–5% over last 18 months — oversupply real",
      "Mixed safety perception on Elizabeth + Lonsdale after 9pm midweek",
    ],
    priceRange: "A$9k–14k / m²",
    confidenceScore: 82,
    tags: ["cbd", "transit", "apartment-heavy"],
    sources: [
      { label: "CoreLogic resale index 2025" },
      { label: "PTV tram throughput" },
    ],
    priceData: {
      // Houses rare in CBD core — apartment is the practical stock.
      buyApartmentMedian: 540,
      rentApartmentMedian: 580,
      source: "CoreLogic + realestate.com.au Mar 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "weaker",
      drivers: [
        "Free tram zone + Metro Tunnel (2025) unlocks one-seat commuter pull",
      ],
      risks: [
        "Apartment oversupply — 2015–22 tower build-out depressed resale 3–5%",
        "Owner-occupier ratio low; body-corp votes investor-led (churn, defect risk)",
        "Yields 4.5%+ look attractive but capital growth flat-to-negative since 2018",
      ],
      timeframe: "long",
    },
  },
  {
    id: "mel-southbank",
    name: "Southbank",
    coord: [144.9648, -37.8226],
    verdict: "amber",
    reasons: [
      "NGV + Arts Centre at the door; tram 1/55/58/96 all converge",
      "Defect register flagged ~30% of post-2010 towers — verify the building, not the suburb",
      "Owner-occupier under 40%; tenant churn high, body-corp votes investor-led",
    ],
    priceRange: "A$10k–14k / m²",
    confidenceScore: 78,
    tags: ["waterfront", "cultural", "investor-heavy"],
    sources: [
      { label: "Domain owner-occupier ratio 2025" },
      { label: "VIC BSA defect register" },
    ],
  },
  {
    id: "mel-docklands",
    name: "Docklands",
    coord: [144.9465, -37.8159],
    verdict: "red",
    reasons: [
      "Resales lag CBD by 8–12% — same square metre, weaker demand",
      "Windswept between towers; few cafes open weekends, dead after 7pm Sun",
      "Master plan widely criticised since 2010; no street-scale fix in pipeline",
    ],
    priceRange: "A$8k–12k / m²",
    confidenceScore: 80,
    tags: ["master-planned", "waterfront", "low-amenity"],
    sources: [
      { label: "City of Melbourne activation audit" },
    ],
  },
  {
    id: "mel-carlton",
    name: "Carlton",
    coord: [144.967, -37.8001],
    verdict: "green",
    reasons: [
      "5-min walk to Melbourne Uni; student rentals near-zero vacancy in semester",
      "Lygon St 50+ cafes/bars; Carlton Gardens + Royal Exhibition Bldg next door",
      "Heritage overlay limits extensions/changes; renovations need 6–12 months approval",
    ],
    priceRange: "A$11k–16k / m²",
    confidenceScore: 86,
    tags: ["university", "heritage", "inner-north"],
    sources: [
      { label: "Unimelb enrolment 2025" },
      { label: "REIV median sales" },
    ],
    priceData: {
      buyHouseMedian: 1450,
      buyApartmentMedian: 580,
      rentHouseMedian: 780,
      rentApartmentMedian: 520,
      source: "REIV median sales + realestate.com.au Mar 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "Melbourne Uni (50k+ enrolment) anchors student rental floor",
        "Heritage overlay caps supply — stock scarce, period terraces hold",
        "Metro Tunnel Parkville station delivered 2025 — one-seat to every major node",
      ],
      risks: [
        "Student-market yield lumpy outside Feb–Oct",
        "Renovation approvals 6–12 months under heritage overlay",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "mel-fitzroy",
    name: "Fitzroy",
    coord: [144.978, -37.7983],
    verdict: "green",
    reasons: [
      "Brunswick + Smith St cafes/bars; tram 11 to Collins St in 12 min",
      "Period terrace supply tight; well-renovated 2BRs sell within 30 days",
      "Permit-only parking on most streets; visitor parking near impossible Fri night",
    ],
    priceRange: "A$13k–18k / m²",
    confidenceScore: 84,
    tags: ["cafe-culture", "heritage", "premium-inner"],
    sources: [
      { label: "Yarra City parking density" },
      { label: "Broker sales velocity" },
    ],
    priceData: {
      buyHouseMedian: 1680,
      buyApartmentMedian: 650,
      rentHouseMedian: 850,
      rentApartmentMedian: 560,
      source: "realestate.com.au + Domain Mar 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "Heritage-locked supply; period terrace count hasn't grown in 40 years",
        "Smith/Brunswick St retail mature — cafe-culture premium priced-in, sticky",
        "3–5% capital growth p.a. despite apartment glut elsewhere — demand structural",
      ],
      risks: [
        "Entry ticket A$1.5M+ for period stock — leverage math tighter",
        "Parking scarcity deters family buyers — demand skews young-professional",
      ],
      timeframe: "long",
    },
  },
  {
    id: "mel-collingwood",
    name: "Collingwood",
    coord: [144.9875, -37.8006],
    verdict: "amber",
    reasons: [
      "Warehouse conversions + Cubic, Square offices; startup density highest in Melb",
      "Smith St cafes + bars vibrant; mixed safety reputation north of Johnston after midnight",
      "Light-industrial blocks east of Wellington still pre-conversion — noise + truck movements",
    ],
    priceRange: "A$11k–15k / m²",
    confidenceScore: 76,
    tags: ["warehouse", "startup", "transitional"],
    sources: [
      { label: "VicPol night-precinct data" },
      { label: "Yarra City planning permits" },
    ],
  },
  {
    id: "mel-richmond",
    name: "Richmond",
    coord: [144.9983, -37.8183],
    verdict: "green",
    reasons: [
      "MCG + AAMI in walking distance; trains on Belgrave, Lilydale, Alamein, Glen Waverley",
      "Period townhouses 80–120 sqm common; resale ~30 days, agents lean on auction",
      "Swan St grinds 2 hrs pre/post-match; AFL season locks parking for residents",
    ],
    priceRange: "A$11k–16k / m²",
    confidenceScore: 85,
    tags: ["transit", "sports-precinct", "mature-infra"],
    sources: [
      { label: "PTV train frequency" },
      { label: "REIV sales data 2025" },
    ],
    priceData: {
      buyHouseMedian: 1350,
      buyApartmentMedian: 620,
      rentHouseMedian: 780,
      rentApartmentMedian: 540,
      source: "REIV sales + Domain Mar 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "4 train lines converge — commuter floor across every inner-east corridor",
        "Period stock heritage-listed; Richmond Hill supply fixed",
        "Rental demand structurally young-professional; vacancy <2%",
      ],
      risks: [
        "Apartment tower stock on Swan St / Bridge Rd underperforms period stock",
        "AFL season parking + traffic discounts weekend liveability",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "mel-south-yarra",
    name: "South Yarra",
    coord: [144.9915, -37.84],
    verdict: "green",
    reasons: [
      "Chapel + Toorak Rd cafes, bars, gyms; tram 58/78, train to CBD in 6 min",
      "Melbourne Girls Grammar + Melbourne Grammar zones lock in family demand",
      "Forrest Hill pocket tower-glut; 1BR resales down ~6% vs 2022 peak",
    ],
    priceRange: "A$13k–19k / m²",
    confidenceScore: 88,
    tags: ["premium", "school-catchment", "apartment-heavy"],
    sources: [
      { label: "Stonnington median price index" },
      { label: "DOGS school zone data" },
    ],
    priceData: {
      buyHouseMedian: 2100,
      buyApartmentMedian: 640,
      rentHouseMedian: 1100,
      rentApartmentMedian: 560,
      source: "Stonnington median price index Q1 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "Melbourne Girls Grammar + Melbourne Grammar zones anchor family demand",
        "CBD 6 min by train — commuter premium above Prahran",
      ],
      risks: [
        "Forrest Hill tower glut — 1BR resales down ~6% vs 2022 peak",
        "Apartment vs house bifurcation: houses outperform, apartments stagnate",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "mel-prahran",
    name: "Prahran",
    coord: [144.9929, -37.8518],
    verdict: "green",
    reasons: [
      "Prahran Market Tue/Thu–Sat; 60+ cafes/bars along Greville + Commercial Rd",
      "Tram 6/72/78 + Sandringham line; CBD 10 min, St Kilda beach 8 min",
      "Chapel St noise + partygoers till 3am Fri–Sat; arterial-facing units suffer",
    ],
    priceRange: "A$12k–17k / m²",
    confidenceScore: 82,
    tags: ["transit", "market", "inner-south"],
    sources: [
      { label: "Stonnington rental yield" },
      { label: "Walk Score index" },
    ],
  },
  {
    id: "mel-toorak",
    name: "Toorak",
    coord: [145.0131, -37.8419],
    verdict: "green",
    reasons: [
      "Median house price consistently top 5 AU since 1980s — price floor rock-solid",
      "St Catherine's, Geelong Grammar bus routes; private roads off Toorak Rd",
      "Entry ticket ~$5M+ for a house; apartment market exists but feels out of character",
    ],
    priceRange: "A$18k–35k / m²",
    confidenceScore: 90,
    tags: ["ultra-premium", "heritage", "school-catchment"],
    sources: [
      { label: "REIV top-postcode tracker" },
      { label: "Domain luxury sales 2025" },
    ],
    priceData: {
      buyHouseMedian: 5200,
      buyApartmentMedian: 820,
      rentHouseMedian: 1400,
      rentApartmentMedian: 680,
      source: "REIV top-postcode tracker + Domain luxury 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "Structural top-5 AU postcode — price floor rock-solid since 1980s",
        "Private-school catchment (St Catherine's, Melb Grammar) timeless anchor",
      ],
      risks: [
        "Entry ticket ~A$5M+ for houses — tiny qualified buyer pool, thin liquidity",
        "Yield 1.2–1.8% — capital parking, opportunity-cost high vs Fitzroy/Carlton",
      ],
      timeframe: "long",
    },
  },
  {
    id: "mel-st-kilda",
    name: "St Kilda",
    coord: [144.9797, -37.8677],
    verdict: "amber",
    reasons: [
      "Beach + Luna Park + Acland St cakes; trams 3/16/96 converge",
      "Fitzroy St mixed safety after midnight — long-known soft spot near Grey St",
      "Rental stock swings wild: Art Deco gem next to tired '70s walk-up on same block",
    ],
    priceRange: "A$10k–15k / m²",
    confidenceScore: 79,
    tags: ["beach", "nightlife", "mixed-stock"],
    sources: [
      { label: "Port Phillip Council safety audit" },
    ],
    priceData: {
      buyHouseMedian: 1580,
      buyApartmentMedian: 540,
      rentHouseMedian: 820,
      rentApartmentMedian: 500,
      source: "REIV + realestate.com.au Mar 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "Beach + CBD accessibility unmatched in mid-inner south",
        "Art Deco period stock heritage-controlled; supply fixed",
      ],
      risks: [
        "Fitzroy St safety perception drags short-term rental premium",
        "Stock mixed — '70s walk-ups underperform heritage by 20–30%",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "mel-port-melbourne",
    name: "Port Melbourne",
    coord: [144.9347, -37.8384],
    verdict: "green",
    reasons: [
      "Bay St cafes + Port Melbourne beach; tram 109 to CBD ~18 min off-peak",
      "Townhouses + converted Dunlop/Kraft warehouses; resales under 45 days currently",
      "Tram 109 crawls 30+ min at 5–6pm Fri; no train — bus backup thin",
    ],
    priceRange: "A$12k–17k / m²",
    confidenceScore: 83,
    tags: ["beach", "inner-bayside", "walkable"],
    sources: [
      { label: "Port Phillip median sales" },
      { label: "PTV tram 109 timetable data" },
    ],
  },
  {
    id: "mel-albert-park",
    name: "Albert Park",
    coord: [144.9533, -37.842],
    verdict: "green",
    reasons: [
      "Albert Park Lake + beach; Dundas Pl village has the groceries locals walk to",
      "Stock turnover ~40 sales/yr — tight supply sustains a 5–8% premium over Middle Park",
      "F1 GP early March locks streets for 3 weeks; expect detours + noise",
    ],
    priceRange: "A$14k–19k / m²",
    confidenceScore: 85,
    tags: ["beach", "park-adjacent", "premium"],
    sources: [
      { label: "REIV sales turnover" },
      { label: "City Council event impact report" },
    ],
  },
  {
    id: "mel-middle-park",
    name: "Middle Park",
    coord: [144.9611, -37.851],
    verdict: "green",
    reasons: [
      "Pocket between Albert Park + St Kilda; tram 96 to CBD in 20 min",
      "Middle Park Primary + beach path = families hold on 15+ yrs typically",
      "Tiny market — ~25 sales/yr; if nothing listed, wait or bid aggressively",
    ],
    priceRange: "A$14k–20k / m²",
    confidenceScore: 81,
    tags: ["beach", "family", "low-supply"],
    sources: [
      { label: "Domain scarcity index" },
    ],
  },
  {
    id: "mel-elwood",
    name: "Elwood",
    coord: [144.9854, -37.88],
    verdict: "green",
    reasons: [
      "Art Deco 1930s walk-ups + beach; Ormond Rd cafe strip the weekend anchor",
      "Melbourne Water flood overlay near Elwood Canal — 15+ blocks affected, insurance premiums higher",
      "No train — tram 67 or bus to Balaclava/Ripponlea station; drive time to CBD 20–35 min",
    ],
    priceRange: "A$12k–17k / m²",
    confidenceScore: 80,
    tags: ["beach", "deco-stock", "flood-risk"],
    sources: [
      { label: "Melbourne Water flood overlay" },
      { label: "Port Phillip sales 2025" },
    ],
  },

  // --- South / bayside ---
  {
    id: "mel-brighton",
    name: "Brighton",
    coord: [144.9962, -37.9056],
    verdict: "green",
    reasons: [
      "Bathing boxes + Church St boutiques; Brighton Grammar + Firbank zones",
      "Sandringham-line houses under $3M rare; family resales 30–45 days typical",
      "CBD 25 min via express, 40 min all-stops; no tram in Brighton proper",
    ],
    priceRange: "A$15k–25k / m²",
    confidenceScore: 88,
    tags: ["bayside", "school-catchment", "premium"],
    sources: [
      { label: "Bayside Council median" },
      { label: "Good Schools index" },
    ],
    priceData: {
      buyHouseMedian: 3200,
      buyApartmentMedian: 780,
      rentHouseMedian: 1200,
      rentApartmentMedian: 620,
      source: "Bayside Council + REIV 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "Brighton Grammar + Firbank school zones — family demand never churns",
        "Bayside supply fixed (council bans medium-density on most lots)",
        "Sandringham-line commute stable — 25 min to CBD",
      ],
      risks: [
        "Entry ticket A$3M+ — leverage math tight for yield investors",
        "Yield 1.5–2.0% — capital play, not income",
      ],
      timeframe: "long",
    },
  },
  {
    id: "mel-hampton",
    name: "Hampton",
    coord: [145.0036, -37.9383],
    verdict: "green",
    reasons: [
      "Hampton St village + beach; express to Flinders St in 22 min",
      "~12% cheaper than Brighton for similar Deco/weatherboard stock",
      "Houses still $2M+; first-home buyers priced out of even 2BR units here",
    ],
    priceRange: "A$13k–20k / m²",
    confidenceScore: 83,
    tags: ["bayside", "family", "village"],
    sources: [
      { label: "Bayside Council demography" },
      { label: "REIV sales 2025" },
    ],
  },
  {
    id: "mel-sandringham",
    name: "Sandringham",
    coord: [145.0091, -37.953],
    verdict: "green",
    reasons: [
      "End-of-Sandringham-line; quieter streets, CBD ~30 min",
      "Sandringham Primary + East Sandringham Primary catchments draw young families",
      "Weekend shopping basically Bay Rd strip — Brighton or Southland for anything more",
    ],
    priceRange: "A$12k–18k / m²",
    confidenceScore: 80,
    tags: ["bayside", "family", "end-of-line"],
    sources: [
      { label: "PTV Sandringham line timetable" },
      { label: "Bayside Council median" },
    ],
  },
  {
    id: "mel-mentone",
    name: "Mentone",
    coord: [145.0651, -37.9824],
    verdict: "amber",
    reasons: [
      "Mentone Grammar, St Bede's within 1 km; Frankston line direct to CBD",
      "Nepean Hwy traffic 70+dB till 11pm; west-facing units take the brunt",
      "New apartment supply near station ~200 units/yr — 1BR resales softening",
    ],
    priceRange: "A$9k–14k / m²",
    confidenceScore: 74,
    tags: ["bayside", "school-catchment", "arterial-noise"],
    sources: [
      { label: "Kingston planning permits" },
      { label: "VicRoads Nepean traffic" },
    ],
  },
  {
    id: "mel-cheltenham",
    name: "Cheltenham",
    coord: [145.0547, -37.9683],
    verdict: "amber",
    reasons: [
      "Westfield Southland + Frankston line; Cheltenham to CBD 30 min express",
      "Level-crossing removal completed 2023 cleared the Warrigal Rd AM bottleneck",
      "Industrial + auto-trade blocks east of Warrigal — truck noise 6am weekdays",
    ],
    priceRange: "A$7k–11k / m²",
    confidenceScore: 72,
    tags: ["retail-hub", "infra-upgrade", "mixed-use"],
    sources: [
      { label: "LXRP project register" },
      { label: "Kingston land-use map" },
    ],
  },
  {
    id: "mel-moorabbin",
    name: "Moorabbin",
    coord: [145.0419, -37.9441],
    verdict: "amber",
    reasons: [
      "DFO + South Rd trade strip; logistics, auto, bulk retail jobs stable",
      "Moorabbin Airport flight path 500m-wide noise contour crosses north blocks",
      "Renewal pockets near station since 2022, but most lots still 1960s brick-veneer",
    ],
    priceRange: "A$7k–11k / m²",
    confidenceScore: 70,
    tags: ["industrial", "flight-path", "transit"],
    sources: [
      { label: "Moorabbin Airport noise contour" },
      { label: "Glen Eira/Kingston permits" },
    ],
  },

  // --- South-east / east ---
  {
    id: "mel-caulfield",
    name: "Caulfield",
    coord: [145.0244, -37.8779],
    verdict: "green",
    reasons: [
      "Monash Caulfield + racecourse precinct; Racing Carnival adds ~3 big crowd days/yr",
      "Caulfield station = Frankston + Cranbourne + Pakenham lines interchange",
      "1BR rentals dominated by students — vacancy spikes Dec–Feb between semesters",
    ],
    priceRange: "A$10k–15k / m²",
    confidenceScore: 82,
    tags: ["university", "transit", "rental-heavy"],
    sources: [
      { label: "Monash enrolment 2025" },
      { label: "PTV hub data" },
    ],
  },
  {
    id: "mel-malvern",
    name: "Malvern",
    coord: [145.0285, -37.8582],
    verdict: "green",
    reasons: [
      "Glenferrie Rd village + tram 16; De La Salle + Lauriston Girls in catchment",
      "Family demand steady; Sandringham-line houses trade ~$2.5–4M bracket",
      "Heritage overlay caps supply; new builds rare, scarcity keeps floor firm",
    ],
    priceRange: "A$14k–20k / m²",
    confidenceScore: 86,
    tags: ["school-catchment", "heritage", "premium"],
    sources: [
      { label: "Stonnington median sales" },
      { label: "Good Schools index" },
    ],
  },
  {
    id: "mel-bentleigh",
    name: "Bentleigh",
    coord: [145.0368, -37.9178],
    verdict: "green",
    reasons: [
      "Frankston line + Centre Rd shopping; McKinnon Secondary zone the real draw",
      "Family area — prices up ~6%/yr last 3 yrs, very few surprises",
      "1960s single-storey homes being subdivided into dual-occ — streetscape changing fast",
    ],
    priceRange: "A$10k–14k / m²",
    confidenceScore: 80,
    tags: ["family", "transit", "subdivision-pressure"],
    sources: [
      { label: "Glen Eira permits" },
      { label: "Domain growth index" },
    ],
  },
  {
    id: "mel-chadstone",
    name: "Chadstone",
    coord: [145.082, -37.8864],
    verdict: "amber",
    reasons: [
      "Chadstone SC — 500+ stores, the state's top retail hub for weekend anchors",
      "Nearest train Hughesdale or Oakleigh, 20+ min walk; car-first in daily life",
      "Mall-adjacent 1BR supply 800+ units since 2020 — resales weak, 2-week DOM rare",
    ],
    priceRange: "A$9k–13k / m²",
    confidenceScore: 74,
    tags: ["retail-hub", "car-dependent", "apartment-growth"],
    sources: [
      { label: "Vicinity Centres footfall" },
      { label: "Monash planning data" },
    ],
  },
  {
    id: "mel-clayton",
    name: "Clayton",
    coord: [145.121, -37.9239],
    verdict: "green",
    reasons: [
      "Monash Uni + MMC hospital; rentals near-zero vacancy in term time",
      "SRL East station due here ~2035 — underlying land prices already tracking up",
      "Dandenong + Clayton Rds gridlocked 4:30–6:30pm Mon–Thu; plan 30-min buffer",
    ],
    priceRange: "A$8k–12k / m²",
    confidenceScore: 81,
    tags: ["university", "medical", "infra-upside"],
    sources: [
      { label: "SRL project register" },
      { label: "Monash enrolment 2025" },
    ],
    priceData: {
      buyHouseMedian: 1180,
      buyApartmentMedian: 520,
      rentHouseMedian: 640,
      rentApartmentMedian: 480,
      source: "REIV + Monash MREP 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "stronger",
      drivers: [
        "Suburban Rail Loop East station confirmed — land prices already tracking",
        "Monash Uni (86k) + Monash Medical anchor rental floor year-round",
        "CSIRO + research cluster — stable PhD / researcher tenant pool",
      ],
      risks: [
        "SRL delivery 2035+ — long hold; interim cash flow is the thesis",
        "Dandenong Rd congestion — commute inferior to rail-fed alternatives",
      ],
      timeframe: "long",
    },
  },
  {
    id: "mel-oakleigh",
    name: "Oakleigh",
    coord: [145.0876, -37.9006],
    verdict: "green",
    reasons: [
      "Eaton Mall Greek strip — 20+ souvlaki shops + cafes running since 1970s",
      "Pakenham line + SRL East station planned; land buyers active since 2023",
      "1950s brick homes being knocked down for 3-unit townhouses monthly",
    ],
    priceRange: "A$9k–13k / m²",
    confidenceScore: 78,
    tags: ["cultural", "infra-upside", "subdivision-pressure"],
    sources: [
      { label: "Monash Council permits" },
      { label: "SRL master plan" },
    ],
  },
  {
    id: "mel-glen-waverley",
    name: "Glen Waverley",
    coord: [145.1645, -37.8798],
    verdict: "green",
    reasons: [
      "Glen Waverley Secondary zone drives a 15–20% premium vs neighbours",
      "The Glen redeveloped 2020; end-of-line train, CBD 45 min off-peak",
      "Station-side 1BR supply 500+ units since 2022 — weekday bar scene thin",
    ],
    priceRange: "A$10k–14k / m²",
    confidenceScore: 85,
    tags: ["school-catchment", "end-of-line", "retail-hub"],
    sources: [
      { label: "Monash school zones" },
      { label: "REIV sales 2025" },
    ],
  },
  {
    id: "mel-box-hill",
    name: "Box Hill",
    coord: [145.1224, -37.8194],
    verdict: "green",
    reasons: [
      "Box Hill Central + hospital + TAFE anchor daytime buzz year-round",
      "Train + 20+ bus routes; CBD 25 min express, Deakin + Monash easy bus",
      "Hoardings everywhere since 2019 — 6+ towers under construction through 2027",
    ],
    priceRange: "A$9k–13k / m²",
    confidenceScore: 82,
    tags: ["activity-centre", "transit", "apartment-growth"],
    sources: [
      { label: "Whitehorse planning register" },
      { label: "VIC DOH hospital data" },
    ],
  },
  {
    id: "mel-doncaster",
    name: "Doncaster",
    coord: [145.1256, -37.7872],
    verdict: "amber",
    reasons: [
      "Westfield Doncaster + ridgeline city views from upper streets",
      "No train — DART bus to CBD 35–50 min, car via Eastern Fwy 30 min off-peak",
      "East Doncaster Secondary zone + solid brick-veneer 1970s stock, renos common",
    ],
    priceRange: "A$9k–13k / m²",
    confidenceScore: 75,
    tags: ["retail-hub", "no-train", "family"],
    sources: [
      { label: "Manningham bus routes" },
      { label: "Westfield footfall" },
    ],
  },
  {
    id: "mel-kew",
    name: "Kew",
    coord: [145.0322, -37.8067],
    verdict: "green",
    reasons: [
      "Trinity Grammar, MLC, Xavier, Ruyton in walking radius",
      "Studley Park boathouse + Yarra trail on doorstep; tram 16/48/109 service",
      "No train in Kew proper — Eastern Fwy + tram only; peak commute 35–45 min",
    ],
    priceRange: "A$13k–19k / m²",
    confidenceScore: 85,
    tags: ["school-catchment", "premium", "tram-only"],
    sources: [
      { label: "Boroondara median sales" },
      { label: "Good Schools index" },
    ],
  },
  {
    id: "mel-hawthorn",
    name: "Hawthorn",
    coord: [145.0355, -37.8222],
    verdict: "green",
    reasons: [
      "Swinburne Uni + Glenferrie Rd cafes + bars; tram 16/48/75",
      "Trains on Alamein, Belgrave, Lilydale — CBD in 12–15 min express",
      "Student-share housing dense around campus — weekday night noise on Power/Church Sts",
    ],
    priceRange: "A$12k–17k / m²",
    confidenceScore: 83,
    tags: ["university", "transit", "inner-east"],
    sources: [
      { label: "Swinburne enrolment" },
      { label: "PTV service data" },
    ],
  },
  {
    id: "mel-camberwell",
    name: "Camberwell",
    coord: [145.0691, -37.8258],
    verdict: "green",
    reasons: [
      "Camberwell High + Canterbury Girls zones; Lyall St cafes steady for 20+ yrs",
      "Sunday market on Station St since 1976 — still the weekend meeting point",
      "Burke + Camberwell junction gridlocks 10am–6pm Sat; avoid weekend errands",
    ],
    priceRange: "A$12k–18k / m²",
    confidenceScore: 84,
    tags: ["family", "school-catchment", "retail-hub"],
    sources: [
      { label: "Boroondara sales 2025" },
      { label: "REIV growth index" },
    ],
  },
  {
    id: "mel-balwyn",
    name: "Balwyn",
    coord: [145.0794, -37.8099],
    verdict: "green",
    reasons: [
      "Balwyn High zone: houses inside boundary trade at 15–25% premium to outside",
      "Tree-lined streets; inter-war brick stock 600–900 sqm blocks typical",
      "No train — tram 109 on Whitehorse Rd; car via Eastern Fwy the practical route",
    ],
    priceRange: "A$13k–19k / m²",
    confidenceScore: 86,
    tags: ["school-catchment", "premium", "family"],
    sources: [
      { label: "Balwyn High zone map" },
      { label: "Domain premium index" },
    ],
  },
  {
    id: "mel-burwood",
    name: "Burwood",
    coord: [145.114, -37.85],
    verdict: "amber",
    reasons: [
      "Deakin Burwood campus + tram 75; 1BR rents ₹550–650/wk, students anchor demand",
      "Burwood Hwy + Toorak Rd arterial noise till 11pm — non-arterial streets quieter",
      "Subdivision + townhouse infill active since 2019; streetscape churning month-to-month",
    ],
    priceRange: "A$8k–12k / m²",
    confidenceScore: 74,
    tags: ["university", "arterial", "infill"],
    sources: [
      { label: "Deakin enrolment" },
      { label: "Whitehorse planning" },
    ],
  },
  {
    id: "mel-mount-waverley",
    name: "Mount Waverley",
    coord: [145.1303, -37.8756],
    verdict: "green",
    reasons: [
      "Mt Waverley Secondary zone drives family demand; Asian-heritage buyers anchor it",
      "Glen Waverley line; Monash Fwy in 5 min, CBD 35 min off-peak by car",
      "1960s brick-veneer stock — budget $150k–300k for proper renovation",
    ],
    priceRange: "A$9k–13k / m²",
    confidenceScore: 80,
    tags: ["family", "school-catchment", "transit"],
    sources: [
      { label: "Monash school zones" },
      { label: "REIV sales 2025" },
    ],
  },
  {
    id: "mel-blackburn",
    name: "Blackburn",
    coord: [145.1508, -37.8196],
    verdict: "green",
    reasons: [
      "Blackburn Lake + 40%+ canopy cover; quiet family streets",
      "Belgrave/Lilydale line + Eastern Fwy in 3 min; CBD 30 min off-peak",
      "South Parade + station shops feel dated — new cafes rare, Box Hill absorbs spend",
    ],
    priceRange: "A$9k–13k / m²",
    confidenceScore: 78,
    tags: ["family", "green-cover", "transit"],
    sources: [
      { label: "Whitehorse parks register" },
      { label: "Domain growth index" },
    ],
  },
  {
    id: "mel-ringwood",
    name: "Ringwood",
    coord: [145.2283, -37.814],
    verdict: "amber",
    reasons: [
      "Eastland + train terminus; Maroondah Hwy upgrade 2023 improved car commute",
      "Long commute: CBD 40 min express train, 50+ min peak by car",
      "New 1BR apartments near station resold below 2022 peak — absorption slow",
    ],
    priceRange: "A$7k–11k / m²",
    confidenceScore: 72,
    tags: ["activity-centre", "outer-east", "commute-long"],
    sources: [
      { label: "Maroondah planning register" },
      { label: "Eastland footfall" },
    ],
  },

  // --- West ---
  {
    id: "mel-footscray",
    name: "Footscray",
    coord: [144.9, -37.7998],
    verdict: "amber",
    reasons: [
      "Little Saigon market + 100+ Vietnamese/African eateries; Hopkins St cafes booming since 2022",
      "Werribee, Sunbury, Williamstown lines + V/Line; CBD 12 min express",
      "Mixed safety reputation around station after 9pm; verify the exact street",
    ],
    priceRange: "A$8k–12k / m²",
    confidenceScore: 75,
    tags: ["gentrifying", "cultural", "transit"],
    sources: [
      { label: "Maribyrnong demography" },
      { label: "VicPol precinct report" },
    ],
  },
  {
    id: "mel-west-footscray",
    name: "West Footscray",
    coord: [144.8835, -37.7949],
    verdict: "amber",
    reasons: [
      "~12% cheaper than Footscray for similar Edwardian stock",
      "Sunshine line train + Sunshine Rd; CBD 18 min, Sunshine 7 min",
      "Rat-running on Essex St + Eleanor St 7–9am; school-zone traffic stress",
    ],
    priceRange: "A$7k–10k / m²",
    confidenceScore: 70,
    tags: ["gentrifying", "value", "arterial"],
    sources: [
      { label: "Maribyrnong sales 2025" },
      { label: "VicRoads LGA data" },
    ],
  },
  {
    id: "mel-yarraville",
    name: "Yarraville",
    coord: [144.8924, -37.8159],
    verdict: "green",
    reasons: [
      "Anderson St village + Sun Theatre + monthly Yarraville Festival",
      "Edwardian/Federation cottages 200–400 sqm; resales 21–35 days currently",
      "Francis St trucks to Port — curfew 10pm–6am, daytime rumble continuous",
    ],
    priceRange: "A$10k–14k / m²",
    confidenceScore: 81,
    tags: ["village", "family", "port-adjacent"],
    sources: [
      { label: "Maribyrnong truck curfew data" },
      { label: "REIV sales" },
    ],
  },
  {
    id: "mel-seddon",
    name: "Seddon",
    coord: [144.8897, -37.8073],
    verdict: "green",
    reasons: [
      "Tiny 1.5 km² wedge between Footscray + Yarraville; train to CBD 14 min",
      "Charles St village + Mr West cafe; walk-to-everything radius",
      "<60 house sales/yr — very tight supply, prices up 25% in 4 yrs",
    ],
    priceRange: "A$11k–14k / m²",
    confidenceScore: 78,
    tags: ["village", "tight-supply", "inner-west"],
    sources: [
      { label: "Maribyrnong Council sales" },
      { label: "Domain growth index" },
    ],
  },
  {
    id: "mel-newport",
    name: "Newport",
    coord: [144.8836, -37.8433],
    verdict: "green",
    reasons: [
      "Edwardian + Cal-bungalow stock; Mason St shops slowly upscaling since 2021",
      "Williamstown line + Newport ferry-adjacent; CBD 25 min off-peak",
      "Mobil refinery 3 km west — periodic flare events visible at night",
    ],
    priceRange: "A$9k–13k / m²",
    confidenceScore: 77,
    tags: ["heritage", "bayside", "industrial-adjacent"],
    sources: [
      { label: "Hobsons Bay planning" },
      { label: "REIV median" },
    ],
  },
  {
    id: "mel-williamstown",
    name: "Williamstown",
    coord: [144.899, -37.8614],
    verdict: "green",
    reasons: [
      "Nelson Pl maritime strip + Williamstown Beach with full CBD skyline view",
      "Ferry to Docklands weekends; train to CBD 30 min via Newport",
      "Single train line — Sunbury/Werribee disruptions cascade; tram-free area",
    ],
    priceRange: "A$11k–16k / m²",
    confidenceScore: 82,
    tags: ["heritage", "waterfront", "single-line"],
    sources: [
      { label: "Hobsons Bay tourism data" },
      { label: "Domain premium index" },
    ],
  },
  {
    id: "mel-altona",
    name: "Altona",
    coord: [144.8292, -37.8695],
    verdict: "amber",
    reasons: [
      "Altona Beach + Pier St shops; quiet family streets, low turnover",
      "Werribee line; CBD 30 min off-peak, 45 min peak",
      "Mobil + Qenos petrochem 2 km west — mixed safety perception around night flare events",
    ],
    priceRange: "A$8k–11k / m²",
    confidenceScore: 71,
    tags: ["beach", "industrial-adjacent", "family"],
    sources: [
      { label: "EPA air quality station" },
      { label: "Hobsons Bay Council" },
    ],
  },
  {
    id: "mel-sunshine",
    name: "Sunshine",
    coord: [144.8325, -37.7869],
    verdict: "amber",
    reasons: [
      "Sunshine becomes SRL West + Melbourne Airport Rail interchange ~2030",
      "Land buyers active; entry homes still under $600k currently",
      "Mixed safety perception around station evenings — verify the street, day and night",
    ],
    priceRange: "A$6k–9k / m²",
    confidenceScore: 72,
    tags: ["infra-upside", "value", "activity-centre"],
    sources: [
      { label: "SRL + airport rail plan" },
      { label: "Brimbank planning" },
    ],
  },
  {
    id: "mel-werribee",
    name: "Werribee",
    coord: [144.6557, -37.9002],
    verdict: "red",
    reasons: [
      "Far west; 4BR new builds still under $700k currently",
      "Werribee line peak-hour standing-room only; Princes Fwy 60+ min to CBD",
      "Schools + hospitals lag pop curve — Werribee Mercy under serious load",
    ],
    priceRange: "A$5k–8k / m²",
    confidenceScore: 74,
    tags: ["growth-corridor", "long-commute", "infra-lag"],
    sources: [
      { label: "Wyndham infrastructure audit" },
      { label: "VicRoads Princes Fwy data" },
    ],
  },
  {
    id: "mel-point-cook",
    name: "Point Cook",
    coord: [144.7476, -37.9202],
    verdict: "amber",
    reasons: [
      "Saltwater Coast + Sanctuary Lakes estates; 5 new primary schools opened since 2018",
      "No train inside suburb — bus 495/498 to Williams Landing 12–18 min",
      "Point Cook Rd peak crawl 4:30–6:30pm — single-arterial bottleneck well-known",
    ],
    priceRange: "A$6k–9k / m²",
    confidenceScore: 73,
    tags: ["master-planned", "no-train", "growth"],
    sources: [
      { label: "Wyndham demography" },
      { label: "VicRoads congestion report" },
    ],
  },
  {
    id: "mel-tarneit",
    name: "Tarneit",
    coord: [144.6881, -37.8364],
    verdict: "red",
    reasons: [
      "Pop grew ~15%/yr last 3 yrs; under-35 family demographic anchor",
      "Tarneit station + Regional Rail; CBD 35 min on V/Line, 50+ on Metro",
      "School over-enrolment chronic — primary catchments closing yearly to non-locals",
    ],
    priceRange: "A$5k–8k / m²",
    confidenceScore: 72,
    tags: ["growth-corridor", "infra-lag", "young-families"],
    sources: [
      { label: "VIC DE school enrolment" },
      { label: "Wyndham permits" },
    ],
  },

  // --- North / inner-north ---
  {
    id: "mel-north-melbourne",
    name: "North Melbourne",
    coord: [144.9408, -37.7982],
    verdict: "green",
    reasons: [
      "20-min walk to CBD via Queen Vic Market; Errol St cafes pre-9am crowd",
      "Single-fronted Victorian terraces dominate — 80–120 sqm typical",
      "Homes Vic public-housing tower renewal ongoing through 2028 — construction nearby",
    ],
    priceRange: "A$11k–15k / m²",
    confidenceScore: 80,
    tags: ["inner-city", "heritage", "renewal"],
    sources: [
      { label: "Homes Victoria renewal plan" },
      { label: "Melbourne City median" },
    ],
  },
  {
    id: "mel-parkville",
    name: "Parkville",
    coord: [144.9516, -37.7847],
    verdict: "green",
    reasons: [
      "Royal Melbourne, Royal Womens, Peter Mac + Unimelb walk-distance",
      "Metro Tunnel Parkville station opens 2025 — direct CBD/Sunbury/Cranbourne access",
      "85%+ apartment stock; family-sized 3BRs almost non-existent here",
    ],
    priceRange: "A$11k–15k / m²",
    confidenceScore: 83,
    tags: ["university", "medical", "metro-tunnel"],
    sources: [
      { label: "Metro Tunnel project" },
      { label: "Unimelb campus data" },
    ],
  },
  {
    id: "mel-brunswick",
    name: "Brunswick",
    coord: [144.9611, -37.7668],
    verdict: "green",
    reasons: [
      "Sydney Rd 5km of cafes/bars/Mediterranean grocers; Bar Romantica anchor since '80s",
      "Tram 19 + Upfield line + Capital City Trail bike path; CBD 18 min by tram",
      "Constant tower construction along Sydney Rd since 2019 — dust + crane noise common",
    ],
    priceRange: "A$10k–14k / m²",
    confidenceScore: 80,
    tags: ["creative", "tram-corridor", "bike"],
    sources: [
      { label: "Moreland planning" },
      { label: "Upfield path counts" },
    ],
  },
  {
    id: "mel-coburg",
    name: "Coburg",
    coord: [144.9653, -37.7425],
    verdict: "green",
    reasons: [
      "Pentridge prison redevelopment + new cinema/cafes opened since 2022",
      "Upfield line; CBD 22 min, Brunswick 5 min — bike commute popular",
      "Lots of 1950s walk-ups mid-renovation — building activity high through 2027",
    ],
    priceRange: "A$8k–12k / m²",
    confidenceScore: 77,
    tags: ["gentrifying", "transit", "heritage"],
    sources: [
      { label: "Moreland sales 2025" },
      { label: "Pentridge masterplan" },
    ],
  },
  {
    id: "mel-preston",
    name: "Preston",
    coord: [145.0142, -37.7406],
    verdict: "amber",
    reasons: [
      "Preston Market + ~15% cheaper than Northcote for similar period stock",
      "Mernda line + tram 11/86; CBD 25 min train, 35 min tram",
      "High St station infill 400+ apartments since 2020; some streets still industrial-feeling",
    ],
    priceRange: "A$8k–12k / m²",
    confidenceScore: 74,
    tags: ["value", "transit", "infill"],
    sources: [
      { label: "Darebin planning" },
      { label: "REIV sales" },
    ],
  },
  {
    id: "mel-northcote",
    name: "Northcote",
    coord: [144.9998, -37.7707],
    verdict: "green",
    reasons: [
      "High St + Westgarth: 80+ cafes/bars + Northcote Social Club venue",
      "Tram 86 + Mernda line + bike trails; CBD 18–22 min",
      "Houses now $1.6–2.5M; first-home buyers priced out, families competing",
    ],
    priceRange: "A$11k–15k / m²",
    confidenceScore: 83,
    tags: ["cafe-culture", "family", "inner-north"],
    sources: [
      { label: "Darebin sales 2025" },
      { label: "REIV growth index" },
    ],
  },
  {
    id: "mel-thornbury",
    name: "Thornbury",
    coord: [145.0025, -37.7574],
    verdict: "green",
    reasons: [
      "Quieter than Northcote; cottage stock holds 5–8% discount currently",
      "Tram 11 + Mernda line; CBD 24 min, Capital City bike trail edge",
      "Outside High St strip, weekday dining options thin — most close 9pm",
    ],
    priceRange: "A$10k–14k / m²",
    confidenceScore: 80,
    tags: ["village", "bike", "tram-corridor"],
    sources: [
      { label: "Darebin permits" },
      { label: "Domain median" },
    ],
  },
  {
    id: "mel-heidelberg",
    name: "Heidelberg",
    coord: [145.068, -37.758],
    verdict: "amber",
    reasons: [
      "Austin + Mercy + Warringal hospitals = stable professional renter pool",
      "Hilly topography — narrow Lower Heidelberg Rd lanes + cycling climbs",
      "Hurstbridge line; CBD 32 min, but bus + parking patchy off the spine",
    ],
    priceRange: "A$9k–13k / m²",
    confidenceScore: 75,
    tags: ["medical", "topographic", "family"],
    sources: [
      { label: "VIC DOH Austin data" },
      { label: "Banyule planning" },
    ],
  },
  {
    id: "mel-ivanhoe",
    name: "Ivanhoe",
    coord: [145.0445, -37.77],
    verdict: "green",
    reasons: [
      "Upper Heidelberg Rd village + Ivanhoe Grammar; tree canopy 35%+",
      "Hurstbridge line + bus spine; Eastern Fwy in 5 min",
      "Train signal failures on Hurstbridge line common; commute reliability the weak spot",
    ],
    priceRange: "A$11k–15k / m²",
    confidenceScore: 81,
    tags: ["family", "village", "leafy"],
    sources: [
      { label: "Banyule demography" },
      { label: "REIV sales" },
    ],
  },
  {
    id: "mel-reservoir",
    name: "Reservoir",
    coord: [145.01, -37.72],
    verdict: "amber",
    reasons: [
      "500–700 sqm blocks common; subdivision potential drives investor interest",
      "Mernda line grade-separation 2022 cleared the AM jam at Bell St",
      "1960s brick-veneer next to fresh townhouses — streetscape uneven",
    ],
    priceRange: "A$7k–11k / m²",
    confidenceScore: 73,
    tags: ["value", "gentrifying", "large-blocks"],
    sources: [
      { label: "Darebin permits" },
      { label: "Mernda-line works" },
    ],
  },
  {
    id: "mel-eltham",
    name: "Eltham",
    coord: [145.145, -37.715],
    verdict: "green",
    reasons: [
      "Bush-suburb feel; mudbrick heritage homes from Montsalvat era still trade",
      "Hurstbridge line; CBD 45 min, but bushland trails on doorstep",
      "CFA bushfire overlay covers ~30% of suburb — insurance higher, BAL ratings matter",
    ],
    priceRange: "A$8k–12k / m²",
    confidenceScore: 77,
    tags: ["bush-suburb", "heritage", "bushfire-risk"],
    sources: [
      { label: "CFA overlay map" },
      { label: "Nillumbik sales" },
    ],
  },
  {
    id: "mel-greensborough",
    name: "Greensborough",
    coord: [145.1025, -37.7042],
    verdict: "amber",
    reasons: [
      "Greensborough Plaza + Hurstbridge line terminus; family demand stable",
      "Hilly streets + older 1970s brick; renovations cap 30–40% of recent sales",
      "CBD 45–55 min peak by train — long commute is the trade-off for the space",
    ],
    priceRange: "A$7k–10k / m²",
    confidenceScore: 72,
    tags: ["family", "retail-hub", "outer-north"],
    sources: [
      { label: "Banyule plaza footfall" },
      { label: "PTV line data" },
    ],
  },

  // --- Outer south-east ---
  {
    id: "mel-dandenong",
    name: "Dandenong",
    coord: [145.214, -37.9871],
    verdict: "red",
    reasons: [
      "Pakenham + Cranbourne lines interchange; South East's main activity centre",
      "Mixed safety perception around station and Walker St pedestrian mall after dark",
      "Major industrial base — warehouse + manufacturing jobs but freight traffic till 10pm",
    ],
    priceRange: "A$5k–8k / m²",
    confidenceScore: 73,
    tags: ["activity-centre", "industrial", "diverse"],
    sources: [
      { label: "VicPol precinct data" },
      { label: "Greater Dandenong demography" },
    ],
  },
  {
    id: "mel-springvale",
    name: "Springvale",
    coord: [145.1527, -37.951],
    verdict: "amber",
    reasons: [
      "Springvale Rd Vietnamese strip — 40+ pho + banh mi shops, the real deal",
      "Pakenham line + planned SRL interchange ~2035; long-dated upside",
      "Housing stock mostly 1970s brick-veneer — most buyers factor a reno in",
    ],
    priceRange: "A$6k–9k / m²",
    confidenceScore: 74,
    tags: ["cultural", "transit", "food-scene"],
    sources: [
      { label: "Greater Dandenong plan" },
      { label: "SRL alignment map" },
    ],
  },

  // --- Expansion batch 1: inner / bayside premium ---
  {
    id: "mel-east-melbourne",
    name: "East Melbourne",
    coord: [144.9841, -37.8124],
    verdict: "green",
    reasons: [
      "Fitzroy Gardens + Treasury Gardens; consular district, extremely quiet",
      "Walk to CBD + MCG in 15 min; no tower construction since 2010",
      "Terraces routinely cross $3M; liquidity below that price point is thin",
    ],
    priceRange: "A$16k–22k / m²",
    confidenceScore: 86,
    tags: ["heritage", "premium-inner", "leafy", "walkable"],
    sources: [
      { label: "Heritage Victoria register" },
      { label: "Domain median tracker 2025" },
    ],
  },
  {
    id: "mel-armadale",
    name: "Armadale",
    coord: [144.9952, -37.8551],
    verdict: "green",
    reasons: [
      "High St Armadale — 80+ boutiques/cafes/restaurants; tram 6 + train",
      "Prestige inner-east; houses rarely sub-$2.5M even for dual-occ",
      "~70% apartment stock built post-2000; family 3BR houses scarce",
    ],
    priceRange: "A$13k–18k / m²",
    confidenceScore: 84,
    tags: ["premium-inner", "tram-corridor", "cafe-culture"],
    sources: [
      { label: "Stonnington planning 2025" },
      { label: "CoreLogic tram-corridor index" },
    ],
  },
  {
    id: "mel-canterbury",
    name: "Canterbury",
    coord: [145.0762, -37.8223],
    verdict: "green",
    reasons: [
      "Canterbury Girls + Camberwell Grammar zones; premium 10–15% over outside-zone",
      "Maling Rd heritage village — 1920s shopfronts, cafes on weekends since the '90s",
      "Entry house $2.5M+; first-home buyers effectively locked out",
    ],
    priceRange: "A$14k–20k / m²",
    confidenceScore: 85,
    tags: ["family", "school-catchment", "leafy", "mature-infra"],
    sources: [
      { label: "VIC DET zone map" },
      { label: "Domain school-premium index" },
    ],
  },
  {
    id: "mel-surrey-hills",
    name: "Surrey Hills",
    coord: [145.0993, -37.8231],
    verdict: "green",
    reasons: [
      "Belgrave/Lilydale line; CBD 22 min express, Box Hill 5 min",
      "Our Lady of Sion + Surrey Hills Primary anchor family demand",
      "No real retail strip — groceries + cafes in Canterbury or Box Hill",
    ],
    priceRange: "A$11k–15k / m²",
    confidenceScore: 81,
    tags: ["family", "school-catchment", "transit", "quiet"],
    sources: [
      { label: "Whitehorse demography" },
      { label: "Metro Trains patronage 2025" },
    ],
  },
  {
    id: "mel-eaglemont",
    name: "Eaglemont",
    coord: [145.0615, -37.7611],
    verdict: "green",
    reasons: [
      "1930s Griffin-planned estate + Yarra River escarpment views",
      "Heidelberg rail 8 min walk; Ivanhoe shops a short drive",
      "Tiny micro-market (~20 sales/yr) — be patient and expect to overpay at auction",
    ],
    priceRange: "A$12k–17k / m²",
    confidenceScore: 72,
    tags: ["heritage", "leafy", "premium-inner"],
    sources: [
      { label: "Banyule heritage overlay" },
      { label: "Domain micro-market data" },
    ],
  },
  {
    id: "mel-kooyong",
    name: "Kooyong",
    coord: [145.0272, -37.8378],
    verdict: "green",
    reasons: [
      "Kooyong Tennis Club + Gardiner's Creek bike trail on doorstep",
      "Federation + Edwardian character stock; blocks often 700–1000 sqm",
      "<15 house sales/yr; comparables thin, wait 3–6 months for the right listing",
    ],
    priceRange: "A$15k–20k / m²",
    confidenceScore: 76,
    tags: ["premium-inner", "leafy", "quiet"],
    sources: [
      { label: "Stonnington overlay map" },
      { label: "CoreLogic turnover 2024–25" },
    ],
  },
  {
    id: "mel-black-rock",
    name: "Black Rock",
    coord: [145.0136, -37.9762],
    verdict: "green",
    reasons: [
      "Red Bluff cliffs + coastal walk; quiet family streets, low turnover",
      "Sandringham College + Beaumaris Primary zones; long-term owners 15+ yrs typical",
      "No train — bus 822/823 to Sandringham, otherwise car via Beach Rd",
    ],
    priceRange: "A$11k–15k / m²",
    confidenceScore: 79,
    tags: ["beach", "family", "quiet", "bus-only"],
    sources: [
      { label: "Bayside council plan" },
      { label: "VIC DET catchment" },
    ],
  },
  {
    id: "mel-beaumaris",
    name: "Beaumaris",
    coord: [145.0336, -37.9855],
    verdict: "green",
    reasons: [
      "Ricketts Point beach + notable mid-century modernist homes (Boyd, Romberg)",
      "Family-skewed — primary schools oversubscribed 2 yrs running",
      "No train; Mentone station 10 min drive — daily car use is the norm",
    ],
    priceRange: "A$10k–14k / m²",
    confidenceScore: 80,
    tags: ["beach", "family", "heritage", "car-dependent"],
    sources: [
      { label: "Bayside heritage study" },
      { label: "Domain owner-retention index" },
    ],
  },

  // --- Expansion batch 2: budget / working-belt ---
  {
    id: "mel-broadmeadows",
    name: "Broadmeadows",
    coord: [144.9193, -37.6834],
    verdict: "red",
    reasons: [
      "Houses under $500k still possible currently — Melbourne's lowest median",
      "Craigieburn line; CBD 35 min train — ring-route bus network practical",
      "Safety perception varies block to block — walk the street day and night",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 71,
    tags: ["industrial-adjacent", "transit", "infra-lag"],
    sources: [
      { label: "Hume council plan 2025" },
      { label: "VicPol precinct data" },
    ],
  },
  {
    id: "mel-meadow-heights",
    name: "Meadow Heights",
    coord: [144.929, -37.6642],
    verdict: "red",
    reasons: [
      "4BR double-garage homes still under $550k currently",
      "Mixed safety reputation — reports vary sharply by cul-de-sac, visit at night",
      "No train — bus 531/542 to Broadmeadows station the only non-car option",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 66,
    tags: ["bus-only", "infra-lag"],
    sources: [
      { label: "Hume demography snapshot" },
      { label: "PTV route coverage" },
    ],
  },
  {
    id: "mel-craigieburn",
    name: "Craigieburn",
    coord: [144.9474, -37.5994],
    verdict: "amber",
    reasons: [
      "Craigieburn line terminus; new-build 4BR homes still around $650k",
      "Long commute — 45+ min peak to CBD, Hume Fwy backup from 4:30pm",
      "Town centre thin — Craigieburn Central mall the only real retail anchor",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 73,
    tags: ["growth-corridor", "long-commute", "car-dependent"],
    sources: [
      { label: "Hume growth plan" },
      { label: "Metro Trains timetable" },
    ],
    priceData: {
      buyHouseMedian: 640,
      buyApartmentMedian: 420,
      rentHouseMedian: 520,
      rentApartmentMedian: 430,
      source: "Hume growth plan + REIV 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "Hume growth corridor — Vic planning absorbed 450k+ new residents 2020–30 projection",
        "Affordability — 4BR new-build under A$650k still achievable",
        "Craigieburn line one-seat to CBD",
      ],
      risks: [
        "Oversupply — continuous greenfield releases at Mickleham/Kalkallo next door",
        "Town centre thin; rental premium lags house-price appreciation",
        "Commute 45+ min peak — deters high-income tenants",
      ],
      timeframe: "long",
    },
  },
  {
    id: "mel-roxburgh-park",
    name: "Roxburgh Park",
    coord: [144.9305, -37.6372],
    verdict: "amber",
    reasons: [
      "1990s–2000s estate stock; 3BR homes still sub-$550k currently",
      "Craigieburn line from Roxburgh Park station; CBD 40 min train",
      "Local cafes + shops thin — most amenity trips go to Craigieburn Central",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 68,
    tags: ["growth-corridor", "long-commute", "transit"],
    sources: [
      { label: "Hume council atlas" },
      { label: "PTV coverage" },
    ],
  },
  {
    id: "mel-st-albans",
    name: "St Albans",
    coord: [144.8004, -37.7458],
    verdict: "amber",
    reasons: [
      "Sunbury line + Asian/African food strips — Albert St cafes since the '00s",
      "Grade separation at Furlong Rd completed 2020 — huge AM bottleneck cleared",
      "Post-war brick-veneer dominates; quality swings house-to-house",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 72,
    tags: ["transit", "cultural", "food-scene"],
    sources: [
      { label: "Brimbank economic plan" },
      { label: "LXRP completion register" },
    ],
  },
  {
    id: "mel-deer-park",
    name: "Deer Park",
    coord: [144.77, -37.7703],
    verdict: "amber",
    reasons: [
      "Western Fwy on-ramp + Sunbury line; CBD 25 min train",
      "$500–600k for 3BR common currently; affordability real",
      "Heavy industrial on south + east edges — truck noise + diesel particulates notable",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 69,
    tags: ["industrial-adjacent", "transit", "infra-lag"],
    sources: [
      { label: "Brimbank industrial overlay" },
      { label: "VicRoads freight mapping" },
    ],
  },
  {
    id: "mel-hoppers-crossing",
    name: "Hoppers Crossing",
    coord: [144.7005, -37.8745],
    verdict: "amber",
    reasons: [
      "Established '80s–'90s estate with 600 sqm blocks still common",
      "Werribee line; CBD 35 min train; Princes Fwy backs up 4:30–6:30pm",
      "Amenity revolves around Pacific Werribee; walkable shopping strips thin",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 71,
    tags: ["growth-corridor", "car-dependent", "transit"],
    sources: [
      { label: "Wyndham growth plan" },
      { label: "VicRoads Princes Fwy data" },
    ],
  },
  {
    id: "mel-laverton",
    name: "Laverton",
    coord: [144.7739, -37.8663],
    verdict: "red",
    reasons: [
      "Werribee + Williams Landing lines + Princes Fwy on-ramp; logistics corridor",
      "RAAF base + heavy industrial boundary — truck + air traffic till 11pm",
      "Off Aviation Rd, walkable amenity near-zero; Laverton Market basics only",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 65,
    tags: ["industrial", "transit", "low-amenity"],
    sources: [
      { label: "Wyndham industrial register" },
      { label: "PTV coverage" },
    ],
  },
  {
    id: "mel-melton",
    name: "Melton",
    coord: [144.5823, -37.6812],
    verdict: "amber",
    reasons: [
      "4BR detached homes still under $500k in newer estates currently",
      "Long commute — 60+ min peak to CBD even by V/Line",
      "Town-centre rebuild announced 2022; progress visible but slow month-to-month",
    ],
    priceRange: "A$3k–5k / m²",
    confidenceScore: 68,
    tags: ["growth-corridor", "long-commute", "car-dependent"],
    sources: [
      { label: "Melton council plan" },
      { label: "Metro Trains peak data" },
    ],
  },
  {
    id: "mel-frankston",
    name: "Frankston",
    coord: [145.1254, -38.1438],
    verdict: "amber",
    reasons: [
      "Frankston line terminus + Chisholm TAFE + Monash Peninsula campus",
      "Foreshore $65M revitalisation 2020–24 — Nepean Hwy feel much improved",
      "Mixed safety perception 100m around station after 9pm; pick streets carefully",
    ],
    priceRange: "A$6k–9k / m²",
    confidenceScore: 74,
    tags: ["beach", "end-of-line", "transit", "university"],
    sources: [
      { label: "Frankston council revitalisation plan" },
      { label: "VicPol precinct stats" },
    ],
  },

  // --- Expansion batch 3: growth corridors / developing estates ---
  {
    id: "mel-truganina",
    name: "Truganina",
    coord: [144.7463, -37.8141],
    verdict: "amber",
    reasons: [
      "Turnkey 4BR estate homes ~$700k currently; young-family anchor",
      "No train in suburb; bus 152/153 to Tarneit or Williams Landing",
      "School + retail catch-up ongoing since 2020 — most trips still car-first",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 72,
    tags: ["growth-corridor", "car-dependent", "no-train"],
    sources: [
      { label: "Wyndham growth plan" },
      { label: "VIC DET school pipeline" },
    ],
    priceData: {
      buyHouseMedian: 700,
      buyApartmentMedian: 450,
      rentHouseMedian: 540,
      rentApartmentMedian: 430,
      source: "Wyndham growth plan + realestate.com.au 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "Wyndham absorbed 60k+ residents 2020–25 — structural migration flow",
        "Turnkey 4BR stock ~A$700k — affordability floor pulls first-home buyers",
        "VIC DET school pipeline delivering 3 new primary/secondary 2024–27",
      ],
      risks: [
        "No rail in suburb — commute depends on bus interchange at Tarneit",
        "Supply-heavy: Truganina + Tarneit + Wyndham Vale all releasing in parallel",
      ],
      timeframe: "long",
    },
  },
  {
    id: "mel-wyndham-vale",
    name: "Wyndham Vale",
    coord: [144.6222, -37.8912],
    verdict: "amber",
    reasons: [
      "New housing stock + Wyndham Vale station on Geelong line; CBD 45 min",
      "Geelong line 6–8am standing-only; V/Line bypass services thin",
      "Most amenity at Manor Lakes Central — off-strip, walkable retail still thin",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 70,
    tags: ["growth-corridor", "transit", "infra-lag"],
    sources: [
      { label: "Wyndham growth plan" },
      { label: "V/Line patronage" },
    ],
  },
  {
    id: "mel-mickleham",
    name: "Mickleham",
    coord: [144.9333, -37.5484],
    verdict: "red",
    reasons: [
      "Greenfield estates; 450–600 sqm blocks still under $450k",
      "No train; Hume Fwy 5 min but CBD drive 45+ min peak",
      "Schools + basic retail lagging hard — families schedule drives for every errand",
    ],
    priceRange: "A$3k–5k / m²",
    confidenceScore: 63,
    tags: ["growth-corridor", "no-train", "car-dependent", "infra-lag"],
    sources: [
      { label: "Hume greenfield atlas" },
      { label: "PTV coverage" },
    ],
  },
  {
    id: "mel-kalkallo",
    name: "Kalkallo",
    coord: [144.958, -37.4998],
    verdict: "red",
    reasons: [
      "Post-2018 estates; brand-new 4BR under $600k sometimes",
      "Hume Fwy on-ramp in 3 min; CBD drive 50 min+ peak",
      "No train, nearest shops Craigieburn Central 10 min drive — kid-taxi life",
    ],
    priceRange: "A$3k–5k / m²",
    confidenceScore: 60,
    tags: ["growth-corridor", "no-train", "car-dependent"],
    sources: [
      { label: "Hume growth plan 2050" },
      { label: "VicRoads Hume Fwy data" },
    ],
  },
  {
    id: "mel-cranbourne",
    name: "Cranbourne",
    coord: [145.283, -38.0984],
    verdict: "amber",
    reasons: [
      "Cranbourne line terminus + Cranbourne Park SC; CBD 55 min off-peak",
      "Older '70s brick near centre + newer estates east — mixed streetscape",
      "High St + South Gippsland Hwy gridlock 4:30–6:30pm the standard complaint",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 72,
    tags: ["end-of-line", "transit", "growth-corridor"],
    sources: [
      { label: "Casey council plan" },
      { label: "Metro Trains timetable" },
    ],
  },
  {
    id: "mel-cranbourne-east",
    name: "Cranbourne East",
    coord: [145.306, -38.108],
    verdict: "amber",
    reasons: [
      "Turnkey new-estate 4BR homes still ~$650k; young families dominant",
      "Bus 795/896 to Cranbourne station; no train inside suburb",
      "Primary schools oversubscribed since 2023 — out-of-zone enrolment closed",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 68,
    tags: ["growth-corridor", "bus-only", "family"],
    sources: [
      { label: "Casey growth plan" },
      { label: "VIC DET intake report" },
    ],
  },
  {
    id: "mel-berwick",
    name: "Berwick",
    coord: [145.343, -38.0375],
    verdict: "green",
    reasons: [
      "Mature family suburb; Wilson Botanic Park + Kambrya College catchment",
      "Casey Hospital + Federation Uni campus; strong local employer base",
      "Pakenham line; CBD 55 min peak train — long but dependable",
    ],
    priceRange: "A$6k–9k / m²",
    confidenceScore: 78,
    tags: ["family", "school-catchment", "transit"],
    sources: [
      { label: "Casey community profile" },
      { label: "VIC DET zone map" },
    ],
    priceData: {
      buyHouseMedian: 820,
      buyApartmentMedian: 480,
      rentHouseMedian: 560,
      rentApartmentMedian: 450,
      source: "REIV + Casey council 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "Casey Hospital + Federation Uni campus — stable professional tenant base",
        "Kambrya College catchment + mature retail = family demand sticky",
        "Pakenham-line upgrades (level crossings removed 2022–24) cut commute friction",
      ],
      risks: [
        "CBD 55+ min peak train — caps upside for white-collar commuters",
        "Adjacent Clyde North greenfield stock competes for entry buyers",
      ],
      timeframe: "medium",
    },
  },
  {
    id: "mel-officer",
    name: "Officer",
    coord: [145.4148, -38.0734],
    verdict: "amber",
    reasons: [
      "Greenfield estates + Officer station (2012); new Arena SC opened 2021",
      "Retail strip still assembling — cafes + services week-to-week new",
      "Pakenham line; CBD 65 min train peak — commute is the real cost",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 67,
    tags: ["growth-corridor", "transit", "long-commute"],
    sources: [
      { label: "Cardinia growth plan" },
      { label: "Metro Trains timetable" },
    ],
  },
  {
    id: "mel-clyde-north",
    name: "Clyde North",
    coord: [145.3531, -38.1171],
    verdict: "amber",
    reasons: [
      "Pop grew 20%+/yr since 2019; modern 4BR stock at scale",
      "No train in suburb; bus 894/895 to Cranbourne station",
      "Berwick-Cranbourne Rd chronic congestion 4:30–6:30pm; school-runs compound it",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 69,
    tags: ["growth-corridor", "no-train", "bus-only"],
    sources: [
      { label: "Casey growth plan" },
      { label: "PTV coverage" },
    ],
    priceData: {
      buyHouseMedian: 680,
      buyApartmentMedian: 440,
      rentHouseMedian: 520,
      rentApartmentMedian: 430,
      source: "Casey growth plan + realestate.com.au 2025",
      year: 2025,
    },
    investorEvidence: {
      tier: "moderate",
      drivers: [
        "Fastest-growing suburb in VIC 2019–24 — migration tailwind structural",
        "4BR new-build under A$700k — first-home / growing-family sweet spot",
        "Casey council investing in local retail + school capacity",
      ],
      risks: [
        "No train — future rail (Cranbourne extension) 7–10 years out minimum",
        "Berwick-Cranbourne Rd congestion permanent until capacity upgrade funded",
        "Rental yield lags Craigieburn/Tarneit by ~40bps due to commute cost",
      ],
      timeframe: "long",
    },
  },
  {
    id: "mel-pakenham",
    name: "Pakenham",
    coord: [145.4851, -38.0785],
    verdict: "amber",
    reasons: [
      "Pakenham line terminus + Princes Hwy; 4BR homes still under $650k",
      "Long commute: 70+ min peak train, 75+ min drive during school-run",
      "Town centre revamped 2022; local cafe + retail mix improving month-to-month",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 71,
    tags: ["end-of-line", "transit", "growth-corridor"],
    sources: [
      { label: "Cardinia council plan" },
      { label: "Metro Trains patronage" },
    ],
  },

  // --- Expansion batch 4: risky / amber-red mixed signals ---
  {
    id: "mel-noble-park",
    name: "Noble Park",
    coord: [145.175, -37.967],
    verdict: "amber",
    reasons: [
      "Cranbourne/Pakenham line; Douglas St Vietnamese + Cambodian food strip under A$15",
      "Mixed safety perception around station precinct after 8pm — lived complaint",
      "Older weatherboard stock; unit blocks from late 60s being replaced slowly since 2022",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 70,
    tags: ["transit", "cultural", "food-scene"],
    sources: [
      { label: "Greater Dandenong plan" },
      { label: "VicPol precinct stats" },
    ],
  },
  {
    id: "mel-thomastown",
    name: "Thomastown",
    coord: [145.01, -37.687],
    verdict: "amber",
    reasons: [
      "Mernda line + factory belt; 3BR houses still under A$650k entry",
      "Retail mix tired — High St + Settlement Rd shops slow to refresh",
      "Dalton Rd grinds 7:30–9am; truck movement constant on Mahoneys Rd",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 67,
    tags: ["industrial-adjacent", "transit", "arterial-noise"],
    sources: [
      { label: "Whittlesea industrial register" },
      { label: "VicRoads Dalton Rd data" },
    ],
  },
  {
    id: "mel-lalor",
    name: "Lalor",
    coord: [145.011, -37.668],
    verdict: "amber",
    reasons: [
      "Mernda line + 60s-70s brick veneer stock; median still under A$700k in last 6 months",
      "Infrastructure lag — footpaths and station forecourt overdue for refresh",
      "Train 45–55 min to Flinders St peak; drive via M80 also 50+ min",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 66,
    tags: ["transit", "infra-lag"],
    sources: [
      { label: "Whittlesea plan" },
      { label: "Metro Trains timetable" },
    ],
  },
  {
    id: "mel-cranbourne-west",
    name: "Cranbourne West",
    coord: [145.248, -38.104],
    verdict: "amber",
    reasons: [
      "Post-2015 estates; 4BR new-build still in mid-A$600k band",
      "Industrial-adjacent on Western Port Hwy — truck noise real on west-facing lots",
      "Bus-only to Cranbourne station — 15+ min bus, then 60+ min train peak",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 65,
    tags: ["industrial-adjacent", "growth-corridor", "bus-only"],
    sources: [
      { label: "Casey council overlays" },
      { label: "PTV coverage" },
    ],
  },
  {
    id: "mel-sydenham",
    name: "Sydenham",
    coord: [144.768, -37.707],
    verdict: "amber",
    reasons: [
      "Watergardens shopping + Sunbury line terminus; 35–45 min train to Southern Cross",
      "Long commute vs Footscray/Yarraville — adds 20+ min each way",
      "Family-skewed: school catchments + cul-de-sac estates dominate",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 70,
    tags: ["retail-hub", "transit", "long-commute", "family"],
    sources: [
      { label: "Brimbank retail analysis" },
      { label: "Metro Trains timetable" },
    ],
  },
  {
    id: "mel-taylors-lakes",
    name: "Taylors Lakes",
    coord: [144.774, -37.704],
    verdict: "amber",
    reasons: [
      "Quiet cul-de-sac streets + Taylors Lakes reserve; 4BR family homes A$850k–1.1m",
      "Car-dependent — freeway-dependent commute via Calder Fwy, 35–50 min peak to CBD",
      "Amenity tied to Watergardens — no local high-street feel of its own",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 71,
    tags: ["family", "car-dependent", "quiet"],
    sources: [
      { label: "Brimbank demography" },
      { label: "VicRoads ring-road data" },
    ],
  },

  // --- Expansion batch 5: family / green suburban outer east ---
  {
    id: "mel-mitcham",
    name: "Mitcham",
    coord: [145.191, -37.814],
    verdict: "green",
    reasons: [
      "Grade-separated Lilydale/Belgrave line; 28–35 min train to Flinders St",
      "Strong school-catchment pull — Mullauna, Mitcham PS retain families long-term",
      "House prices up 5–7% in last 6 months; 3BR now commonly A$1.1–1.3m",
    ],
    priceRange: "A$8k–11k / m²",
    confidenceScore: 79,
    tags: ["family", "school-catchment", "transit", "activity-centre"],
    sources: [
      { label: "Whitehorse plan" },
      { label: "LXRP completion register" },
    ],
  },
  {
    id: "mel-vermont",
    name: "Vermont",
    coord: [145.197, -37.834],
    verdict: "green",
    reasons: [
      "Leafy family pocket; Vermont Secondary catchment + Livingstone PS retain buyers",
      "Walk or 8-min drive to Mitcham shops + train; EastLink 5 min via Springvale Rd",
      "Low stock — only 10–14 3BRs listed at a time; patient buyers typical wait 6–8 weeks",
    ],
    priceRange: "A$8k–11k / m²",
    confidenceScore: 78,
    tags: ["family", "leafy", "school-catchment", "quiet"],
    sources: [
      { label: "Whitehorse demography" },
      { label: "VIC DET catchment" },
    ],
  },
  {
    id: "mel-mooroolbark",
    name: "Mooroolbark",
    coord: [145.312, -37.789],
    verdict: "green",
    reasons: [
      "Lilydale line train; 45–55 min to Flinders St, but consistent off-peak runs",
      "Hookey Park + Brushy Creek trail give a village feel — school-catchment families dominate",
      "Commute long: 50+ min train or 55+ min drive via Maroondah Hwy peak",
    ],
    priceRange: "A$6k–9k / m²",
    confidenceScore: 75,
    tags: ["family", "village", "transit", "leafy"],
    sources: [
      { label: "Yarra Ranges plan" },
      { label: "Metro Trains patronage" },
    ],
  },
  {
    id: "mel-croydon",
    name: "Croydon",
    coord: [145.282, -37.799],
    verdict: "green",
    reasons: [
      "Lilydale line + Main St activity centre; town square + library refresh done 2023",
      "Family-skewed — 3BR houses A$900k–1.1m, stable turnover in last 6 months",
      "Express runs to Richmond 40 min peak; drive via EastLink 45–55 min",
    ],
    priceRange: "A$6k–9k / m²",
    confidenceScore: 76,
    tags: ["family", "activity-centre", "transit"],
    sources: [
      { label: "Maroondah plan" },
      { label: "Metro Trains timetable" },
    ],
  },
  {
    id: "mel-donvale",
    name: "Donvale",
    coord: [145.177, -37.788],
    verdict: "green",
    reasons: [
      "Leafy hillside streets + Mullum Mullum Creek parkland; 700–1000 m² blocks common",
      "EastLink 3 min via Tindals Rd — car-dependent; no train, nearest is Heatherdale 12 min drive",
      "4BR family homes typically A$1.4–1.8m; buyers often upgraders from Box Hill/Doncaster",
    ],
    priceRange: "A$8k–11k / m²",
    confidenceScore: 74,
    tags: ["family", "leafy", "quiet", "car-dependent"],
    sources: [
      { label: "Manningham demography" },
      { label: "VicRoads EastLink data" },
    ],
  },
  {
    id: "mel-park-orchards",
    name: "Park Orchards",
    coord: [145.213, -37.768],
    verdict: "green",
    reasons: [
      "Semi-rural character; 1000–2000 m² blocks common — 4BR estate homes A$1.6–2.2m",
      "Quiet school-catchment pocket — Park Orchards PS + Donvale Christian nearby",
      "No train — 10 min drive to Ringwood station; daily car-dependent by default",
    ],
    priceRange: "A$9k–12k / m²",
    confidenceScore: 73,
    tags: ["family", "leafy", "quiet", "no-train", "car-dependent"],
    sources: [
      { label: "Manningham heritage study" },
      { label: "PTV coverage" },
    ],
  },

  // --- Expansion batch 6: outskirts growth corridors (active buyer zones) ---
  {
    id: "mel-epping",
    name: "Epping",
    coord: [145.03, -37.65],
    verdict: "amber",
    reasons: [
      "Mernda line + Pacific Epping centre; 35–45 min train to Melbourne Central",
      "4BR house + land still A$700–800k entry — strong first-home buyer pool in last 6 months",
      "Hume Fwy noise on east edges; freeway-dependent off-peak",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 75,
    tags: ["growth-corridor", "transit", "family", "retail-hub", "freeway-dependent"],
    sources: [
      { label: "Whittlesea growth plan" },
      { label: "Metro Trains timetable" },
    ],
  },
  {
    id: "mel-mernda",
    name: "Mernda",
    coord: [145.097, -37.604],
    verdict: "amber",
    reasons: [
      "Mernda line terminus opened 2018; 45–55 min to CBD, free parking still workable",
      "New-build 4BRs A$720k–850k — family estates, quiet cul-de-sacs",
      "No nightlife, long commute; car needed for anything off the main line",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 73,
    tags: ["growth-corridor", "transit", "family", "school-catchment", "long-commute", "new-build"],
    sources: [
      { label: "Whittlesea growth plan" },
      { label: "Metro Trains patronage" },
    ],
  },
  {
    id: "mel-doreen",
    name: "Doreen",
    coord: [145.139, -37.601],
    verdict: "amber",
    reasons: [
      "Laurimar estate + Doreen town centre; 4BR homes A$780k–950k on 500–600 m² lots",
      "School-catchment draw — Ivanhoe Grammar Plenty + Hazel Glen College retain families",
      "No train — 10 min drive to South Morang or Mernda; weekends jam at Plenty Rd",
    ],
    priceRange: "A$6k–8k / m²",
    confidenceScore: 72,
    tags: ["growth-corridor", "family", "school-catchment", "estate", "no-train", "car-dependent"],
    sources: [
      { label: "Nillumbik council overlays" },
      { label: "Whittlesea growth plan" },
    ],
  },
  {
    id: "mel-wollert",
    name: "Wollert",
    coord: [145.047, -37.591],
    verdict: "amber",
    reasons: [
      "Very new growth estate; 4BR new-builds A$680–780k with full warranty",
      "Shop + school pipeline still rolling out — Edgars Creek Secondary opened 2020",
      "No rail, bus only; 60+ min drive to CBD peak via Epping Rd",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 68,
    tags: ["growth-corridor", "new-build", "estate", "family", "no-train", "bus-only", "infra-lag"],
    sources: [
      { label: "Whittlesea growth plan" },
      { label: "Edgars Creek SC register" },
    ],
  },
  {
    id: "mel-south-morang",
    name: "South Morang",
    coord: [145.096, -37.648],
    verdict: "green",
    reasons: [
      "Mernda line + The Lakes South Morang college; 40 min train to CBD",
      "Family-established estates since 2010; 3BRs consistently in A$650–780k band",
      "Plenty Rd bottleneck 7:30–9am + 5–7pm — lived daily complaint",
    ],
    priceRange: "A$6k–8k / m²",
    confidenceScore: 75,
    tags: ["growth-corridor", "transit", "family", "school-catchment", "arterial-noise"],
    sources: [
      { label: "Whittlesea growth plan" },
      { label: "VicRoads Plenty Rd data" },
    ],
  },
  {
    id: "mel-donnybrook",
    name: "Donnybrook",
    coord: [145.002, -37.546],
    verdict: "amber",
    reasons: [
      "Frontier growth estate on Craigieburn line; house + land from A$580k",
      "Olivine + Kalina estates delivering land releases through 2026",
      "Very little local amenity yet — 10+ min drive to Craigieburn Central for groceries",
    ],
    priceRange: "A$4k–6k / m²",
    confidenceScore: 65,
    tags: ["growth-corridor", "new-build", "estate", "transit", "infra-lag", "low-amenity"],
    sources: [
      { label: "Mitchell Shire growth plan" },
      { label: "Metro Trains timetable" },
    ],
  },
  {
    id: "mel-rockbank",
    name: "Rockbank",
    coord: [144.66, -37.744],
    verdict: "amber",
    reasons: [
      "Ballarat line + Western Fwy; 4BR new-build A$650–780k",
      "Rockbank Middle School + estate centre still maturing — school-catchment forming",
      "Only a handful of trains per hour; freeway-dependent outside rail peak",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 68,
    tags: ["growth-corridor", "transit", "family", "new-build", "single-line", "freeway-dependent"],
    sources: [
      { label: "Melton growth plan" },
      { label: "V/Line timetable" },
    ],
  },
  {
    id: "mel-plumpton",
    name: "Plumpton",
    coord: [144.712, -37.723],
    verdict: "amber",
    reasons: [
      "Newer estate pocket; 4BR homes A$620–720k with double-garage as standard",
      "Plumpton Primary + Catholic Regional College nearby — family-skewed buyer base",
      "No train — nearest is Watergardens 10 min drive; bus network thin",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 67,
    tags: ["growth-corridor", "new-build", "estate", "family", "no-train", "bus-only", "car-dependent"],
    sources: [
      { label: "Melton growth plan" },
      { label: "PTV coverage" },
    ],
  },
  {
    id: "mel-aintree",
    name: "Aintree",
    coord: [144.672, -37.741],
    verdict: "amber",
    reasons: [
      "Woodlea master-planned estate; 4BR new-build A$660–780k, Town Centre 2022",
      "Strong first-home buyer + upgrader mix since 2023 — turnover under 60 days typical",
      "No train — 8 min drive to Rockbank V/Line; freeway-dependent for CBD runs",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 68,
    tags: ["growth-corridor", "new-build", "estate", "family", "no-train", "freeway-dependent"],
    sources: [
      { label: "Melton growth plan" },
      { label: "Woodlea developer register" },
    ],
  },
  {
    id: "mel-williams-landing",
    name: "Williams Landing",
    coord: [144.745, -37.872],
    verdict: "green",
    reasons: [
      "Williams Landing train station + Overland Dr centre; 25–30 min to Southern Cross",
      "Mid-stage growth estate — 3BRs A$750–900k, rentals move in under 3 weeks",
      "Princes Fwy noise on eastern edges; some pockets still hear road 24/7",
    ],
    priceRange: "A$6k–8k / m²",
    confidenceScore: 74,
    tags: ["growth-corridor", "transit", "family", "activity-centre", "arterial-noise"],
    sources: [
      { label: "Wyndham growth plan" },
      { label: "Metro Trains timetable" },
    ],
  },
  {
    id: "mel-manor-lakes",
    name: "Manor Lakes",
    coord: [144.594, -37.907],
    verdict: "amber",
    reasons: [
      "Wyndham Vale line nearby + Manor Lakes shopping centre; 45 min train to CBD",
      "4BR house + land A$620–760k — active first-home buyer zone in last 6 months",
      "Single-line rail + long commute; weekends tied to one retail hub",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 70,
    tags: ["growth-corridor", "transit", "family", "new-build", "single-line", "long-commute"],
    sources: [
      { label: "Wyndham growth plan" },
      { label: "Metro Trains patronage" },
    ],
  },
  {
    id: "mel-diggers-rest",
    name: "Diggers Rest",
    coord: [144.715, -37.623],
    verdict: "amber",
    reasons: [
      "Sunbury line station + semi-rural feel; 4BR homes from A$650k on larger blocks",
      "Small-town character — thin retail, village-pub vibe rather than mall",
      "Long commute: 45+ min train, 45+ min drive via Calder Fwy peak",
    ],
    priceRange: "A$5k–7k / m²",
    confidenceScore: 66,
    tags: ["growth-corridor", "transit", "village", "quiet", "long-commute", "low-amenity"],
    sources: [
      { label: "Melton growth plan" },
      { label: "Metro Trains timetable" },
    ],
  },
];

/**
 * Public MEL verdicts — same records, priceData + investorEvidence
 * back-filled from the pricing bank. See `verdicts.ts` for details.
 */
export const MEL_VERDICTS: Verdict[] = _MEL_RAW.map((v) => {
  // getPricingFor applies monthly overrides on top of the static MEL bank.
  const bank = getPricingFor(v.id);
  if (!bank) return v;
  return {
    ...v,
    priceData: v.priceData ?? bank.priceData,
    investorEvidence: v.investorEvidence ?? bank.investorEvidence,
  };
});
// Suppress "unused import" warning — MEL_SUBURB_PRICING is still re-exported.
void MEL_SUBURB_PRICING;

/**
 * Melbourne-specific nearest-verdict lookup.
 * DUPLICATED from `verdicts.ts#nearestVerdict` to avoid re-exporting a shared
 * implementation — per user constraint, we do not generalize the system.
 */
export function nearestVerdictMel(
  lng: number,
  lat: number,
  maxKm = 3
): Verdict | null {
  let best: Verdict | null = null;
  let bestKm = Infinity;
  for (const v of MEL_VERDICTS) {
    const km = haversineKm(lat, lng, v.coord[1], v.coord[0]);
    if (km < bestKm) {
      bestKm = km;
      best = v;
    }
  }
  return bestKm <= maxKm ? best : null;
}

function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
